import type { UrlType } from './types'

export const URL_TYPE_OPTIONS: Array<{
  value: UrlType
  label: string
  placeholder: string
  singleUse: boolean
}> = [
  {
    value: 'website',
    label: 'Official Site',
    placeholder: 'https://example.com',
    singleUse: true,
  },
  {
    value: 'x',
    label: 'X (Twitter)',
    placeholder: 'https://x.com/username',
    singleUse: true,
  },
  {
    value: 'discord',
    label: 'Discord',
    placeholder: 'https://discord.gg/invite',
    singleUse: true,
  },
  {
    value: 'youtube',
    label: 'YouTube',
    placeholder: 'https://youtube.com/@channel',
    singleUse: true,
  },
  {
    value: 'facebook',
    label: 'Facebook',
    placeholder: 'https://www.facebook.com/page',
    singleUse: true,
  },
  {
    value: 'instagram',
    label: 'Instagram',
    placeholder: 'https://www.instagram.com/handle',
    singleUse: true,
  },
  {
    value: 'reddit',
    label: 'Reddit',
    placeholder: 'https://www.reddit.com/r/subreddit',
    singleUse: true,
  },
  {
    value: 'telegram',
    label: 'Telegram',
    placeholder: 'https://t.me/handle',
    singleUse: true,
  },
  {
    value: 'blog',
    label: 'Blog',
    placeholder: 'https://…',
    singleUse: true,
  },
  {
    value: 'docs',
    label: 'Docs',
    placeholder: 'https://…',
    singleUse: true,
  },
  {
    value: 'custom',
    label: 'Custom',
    placeholder: 'https://…',
    singleUse: false,
  },
]

export const REQUEST_BESPOKE_VALUE = 'Request bespoke'
export const LOCKDROPS_VALUE = 'Lockdrops'
export const ERC4626_DEAL_TYPE = 'ERC-4626 Vault'
export const ERC4626_SALE_TYPE = 'erc4626' as const
export const V1_DEPOSIT_DEAL_TYPE = 'Deposit Sale (v1)'
export const V1_DEPOSIT_SALE_TYPE = 'deposit' as const
export const V1_TVL_DEAL_TYPE = 'TVL Deal (v1)'
export const V1_TVL_SALE_TYPE = 'tvl' as const

/** How a TVL deal turns a deposit into rewards (`v1.tvl.rewardModel.kind`). */
export const V1_TVL_REWARD_KIND_OPTIONS: SalesTypeOption[] = [
  {
    value: 'percentPerMonth',
    label: 'Percent per month',
    description:
      'Rewards accrue as a % of the deposit each month over the lock period.',
  },
  {
    value: 'fixedRatio',
    label: 'Fixed ratio',
    description:
      'Flat conversion: each deposit token converts to a set number of reward tokens.',
  },
  {
    value: 'unquantified',
    label: 'Unquantified',
    description: 'Rewards accrue but no number is promised; shows a label.',
  },
  {
    value: 'weeklyPool',
    label: 'Weekly pool',
    description:
      'A USD pool paid out each week, split pro-rata across total TVL on all chains. No lock period.',
  },
]

/** Deal types configured through the v1 sale-contract form. */
export const isV1ContractDealType = (salesType?: string) =>
  salesType === V1_DEPOSIT_DEAL_TYPE || salesType === V1_TVL_DEAL_TYPE

export type SalesTypeOption = {
  value: string
  label: string
  description?: string
}

export const SALES_TYPE_OPTIONS: SalesTypeOption[] = [
  {
    value: 'Public Sale',
    label: 'Public Sale (IDOs, Node Sale, NFT Sale)',
    description: 'Visible to all verified (KYCed) users on the Deals.',
  },
  {
    value: 'Private Sale',
    label: 'Private Sale (SAFT)',
    description:
      'Visible only to invited (KYCed) users. Requires activation via signature token.',
  },
  {
    value: 'OTC Deal',
    label: 'OTC Deal',
    description: 'Over-the-counter with direct negotiation.',
  },
  {
    value: 'Lockdrops',
    label: 'Lockdrops',
    description: 'Distribution to stakers and locked token holders.',
  },
  {
    value: ERC4626_DEAL_TYPE,
    label: 'Earn — ERC-4626 Vault',
    description:
      'Open-ended direct deposits into an ERC-4626 vault. Listed on Earn, not Deals.',
  },
  {
    value: V1_DEPOSIT_DEAL_TYPE,
    label: 'Deposit Sale (v1 contract)',
    description:
      'Legacy deposit sale backed by an already-deployed v1 sale contract. Deposit, refund and claim windows are configured here.',
  },
  {
    value: V1_TVL_DEAL_TYPE,
    label: 'TVL Deal (v1 contract)',
    description:
      'TVL deal backed by already-deployed v1 sale contracts. Supports deposits on multiple chains, each with its own contract and token.',
  },
  {
    value: REQUEST_BESPOKE_VALUE,
    label: 'Request bespoke',
  },
]

// Filter out contract-dependent deal types when NOT in staging. ERC-4626 is
// intentionally available in every environment because the storefront talks
// directly to the configured vault instead of deploying a sale contract.
export const SALES_TYPE_OPTIONS_FILTERED = SALES_TYPE_OPTIONS.filter(
  (option) =>
    !(
      process.env.REACT_APP_ENV !== 'staging' &&
      (option.value === 'OTC Deal' || option.value === 'Lockdrops')
    ),
)

// PEW TODO: Uncomment the chain id when contract support more chains (https://backend-staging.impossible.finance/api/v2/all-contracts/versions)
// WARNING: DO NOT UNCOMMENT IF CONTRACT YET TO SUPPORT THOSE CHAINS AS IT WILL BREAK THE DEAL API
export const CHAIN_ID_MAP: Record<string, number> = {
  // Ethereum: 1,
  // Base: 8453,
  // Arbitrum: 42161,
  // 'Polygon POS': 137,
  // Optimism: 10,
  // BSC: 56,
  // 'Ethereum Sepolia': 11155111,
  // 'Base Sepolia': 84532,
  'Arbitrum Sepolia': 421614,
  // 'Polygon POS Amoy': 80002,
  // 'Optimism Sepolia': 11155420,
  // 'BSC Testnet': 97,
}

export const DISTRIBUTION_CHAINS: string[] = Object.keys(CHAIN_ID_MAP)

export type DepositStablecoin = {
  value: string
  label: string
  address: string
  decimals: number
}

export const DEPOSIT_CHAIN_STABLECOINS: Record<string, DepositStablecoin[]> = {
  Ethereum: [
    {
      value: 'USDC',
      label: 'USDC',
      address: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
      decimals: 6,
    },
    {
      value: 'USDT',
      label: 'USDT',
      address: '0xdac17f958d2ee523a2206206994597c13d831ec7',
      decimals: 6,
    },
    {
      value: 'DAI',
      label: 'DAI',
      address: '0x6B175474E89094C44Da98b954EedeAC495271d0F',
      decimals: 18,
    },
  ],
  Base: [
    {
      value: 'USDC',
      label: 'USDC',
      address: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',
      decimals: 6,
    },
    {
      value: 'USDT',
      label: 'USDT',
      address: '0xfde4C96c8593536E31F229EA8f37b2ADa2699bb2',
      decimals: 6,
    },
  ],
  Arbitrum: [
    {
      value: 'USDC',
      label: 'USDC',
      address: '0xaf88d065e77c8cC2239327C5EDb3A432268e5831',
      decimals: 6,
    },
    {
      value: 'USDT',
      label: 'USDT',
      address: '0xFd086bC7CD5C481DCC9C85ebE478A1C0b69FCbb9',
      decimals: 6,
    },
    {
      value: 'DAI',
      label: 'DAI',
      address: '0xDA10009cBd5D07dd0CeCc66161FC93D7c9000da1',
      decimals: 18,
    },
  ],
  'Polygon POS': [
    {
      value: 'USDC',
      label: 'USDC',
      address: '0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359',
      decimals: 6,
    },
    {
      value: 'USDT',
      label: 'USDT',
      address: '0xc2132D05D31c914a87C6611C10748AEb04B58e8F',
      decimals: 6,
    },
    {
      value: 'DAI',
      label: 'DAI',
      address: '0x53E0bca35eC356BD5ddDFEbdD1Fc0fD03FaBad39',
      decimals: 18,
    },
  ],
  Optimism: [
    {
      value: 'USDC',
      label: 'USDC',
      address: '0x0b2C639c533813f4Aa9D7837CAf62653d097Ff85',
      decimals: 6,
    },
    {
      value: 'USDT',
      label: 'USDT',
      address: '0x94b008aA00579c1307B0EF2c499aD98a8ce58e58',
      decimals: 6,
    },
    {
      value: 'DAI',
      label: 'DAI',
      address: '0xDA10009cBd5D07dd0CeCc66161FC93D7c9000da1',
      decimals: 18,
    },
  ],
  BSC: [
    {
      value: 'USDC',
      label: 'USDC',
      address: '0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d',
      decimals: 18,
    },
    {
      value: 'USDT',
      label: 'USDT',
      address: '0x55d398326f99059ff775485246999027b3197955',
      decimals: 18,
    },
    {
      value: 'DAI',
      label: 'DAI',
      address: '0x1AF3F329e8BE154074D84B9a0b24fC5e3f1f8459',
      decimals: 18,
    },
  ],
  'Ethereum Sepolia': [
    {
      value: 'USDC',
      label: 'USDC',
      address: '0x1c7D4B196Cb0C7B01d743Fbc6116a902379C7238',
      decimals: 6,
    },
  ],
  'Base Sepolia': [
    {
      value: 'USDC',
      label: 'USDC',
      address: '0x036CbD53842c5426634e7929541eC2318f3dCF7e',
      decimals: 6,
    },
    {
      value: 'IFCASH',
      label: 'IFCASH',
      address: '0xD5E53acADD54c837474d2216FdF1691E4d086275',
      decimals: 18,
    },
  ],
  'Arbitrum Sepolia': [
    {
      value: 'USDC',
      label: 'USDC',
      address: '0x75faf114eafb1BDbe2F0316DF893fd58CE46AA4d',
      decimals: 6,
    },
    {
      value: 'TUSD',
      label: 'TUSD',
      address: '0x51c94B0C9d787d4E16c46b1630D5e791bf40F816',
      decimals: 18,
    },
  ],
  'Polygon POS Amoy': [
    {
      value: 'USDC',
      label: 'USDC',
      address: '0x41E94Eb019C0762f9Bfcf9Fb1E58725BfB0e7582',
      decimals: 6,
    },
  ],
  'Optimism Sepolia': [
    {
      value: 'USDC',
      label: 'USDC',
      address: '0x5fd84259d66Cd46123540766Be93DFE6D43130D7',
      decimals: 6,
    },
  ],
  'BSC Testnet': [
    {
      value: 'USDC',
      label: 'USDC',
      address: '0x64544969ed7EBf5f083679233325356EbE738930',
      decimals: 18,
    },
  ],
}
